"use client";

import { useState, useCallback, useRef, CSSProperties } from "react";
import Image from "next/image";
import { Video, Armchair, MapPin, Loader2 } from "lucide-react";
import { useCalculateLogisticsMutation } from "@/redux/features/divisions/divisionsApi";
import { LogisticsResponse } from "@/types";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { ServiceTheme } from "./BookingStepper";

export interface LocationOption {
  id: string;
  name: string;
  description: string;
  icon: "online" | "in-person" | "at-my-location";
  address?: string;
}

export interface UserLocationData {
  address: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  // Logistics data from API
  logistics?: {
    distance_km: number;
    duration_minutes: number;
    travel_fee: number;
  };
}

interface ServiceLocationProps {
  title?: string;
  subtitle?: string;
  options?: LocationOption[];
  defaultSelectedId?: string;
  onOptionSelect?: (option: LocationOption) => void;
  // New props for NDIS flow
  showAtMyLocation?: boolean;
  onLocationAddressChange?: (location: UserLocationData) => void;
  defaultLocationAddress?: string;
  // Theme prop for red styling (OT/Psychology)
  useRedTheme?: boolean;
  // Theme prop for all service types
  serviceTheme?: ServiceTheme;
  // Counselor ID for logistics calculation
  counselorId?: string;
}

interface ThemePalette {
  primary: string;
  heading: string;
  hoverBg: string;
  selectedBg: string;
  softBorder: string;
  softBg: string;
  selectedShadow: string;
}

const themePalettes: Record<ServiceTheme, ThemePalette> = {
  counseling: {
    primary: "#896deb",
    heading: "#4b3a8f",
    hoverBg: "rgba(255,255,255,0.8)",
    selectedBg: "rgba(137,109,235,0.15)",
    softBorder: "rgba(137,109,235,0.5)",
    softBg: "rgba(137,109,235,0.1)",
    selectedShadow: "0px 0px 0px 7px rgba(137,109,235,0.4)",
  },
  lifeCoaching: {
    primary: "#DDA500",
    heading: "#7a5d00",
    hoverBg: "rgba(255,255,255,0.8)",
    selectedBg: "rgba(221,165,0,0.15)",
    softBorder: "rgba(221,165,0,0.5)",
    softBg: "rgba(221,165,0,0.1)",
    selectedShadow: "0px 0px 0px 7px rgba(221,165,0,0.35)",
  },
  occupationalTherapy: {
    primary: "#C01610",
    heading: "#500300",
    hoverBg: "rgba(255,255,255,0.8)",
    selectedBg: "rgba(192,22,16,0.15)",
    softBorder: "rgba(192,22,16,0.5)",
    softBg: "rgba(192,22,16,0.1)",
    selectedShadow: "0px 0px 0px 7px rgba(192,22,16,0.4)",
  },
  psychology: {
    primary: "#C01610",
    heading: "#500300",
    hoverBg: "rgba(255,255,255,0.8)",
    selectedBg: "rgba(192,22,16,0.15)",
    softBorder: "rgba(192,22,16,0.5)",
    softBg: "rgba(192,22,16,0.1)",
    selectedShadow: "0px 0px 0px 7px rgba(192,22,16,0.4)",
  },
};

const defaultThemePalette: ThemePalette = {
  primary: "#006DFF",
  heading: "#02386D",
  hoverBg: "rgba(255,255,255,0.8)",
  selectedBg: "rgba(0,109,255,0.15)",
  softBorder: "rgba(0,109,255,0.5)",
  softBg: "rgba(0,109,255,0.1)",
  selectedShadow: "0px 0px 0px 7px rgba(0,109,255,0.4)",
};

const defaultOptions: LocationOption[] = [
  {
    id: "online",
    name: "Online Session",
    description:
      "Get online mental health support anytime, anywhere, on your terms.",
    icon: "online",
  },
  {
    id: "in-person",
    name: "In-Person Session",
    description:
      "Meet face-to-face with your practitioner in a comfortable setting.",
    icon: "in-person",
  },
];

const ndisOptions: LocationOption[] = [
  {
    id: "online",
    name: "Online Session",
    description:
      "Get online mental health support anytime, anywhere, on your terms.",
    icon: "online",
  },
  // {
  //   id: "at-my-location",
  //   name: "At my location",
  //   description:
  //     "Get online mental health support anytime, anywhere, on your terms.",
  //   icon: "at-my-location",
  // },
  {
    id: "in-clinic",
    name: "In Clinic Session",
    description:
      "Get online mental health support anytime, anywhere, on your terms.",
    icon: "in-person",
  },
];

const LocationOptionCard = ({
  option,
  isSelected,
  onSelect,
  theme,
}: {
  option: LocationOption;
  isSelected: boolean;
  onSelect: () => void;
  theme: ThemePalette;
}) => {
  const IconComponent =
    option.icon === "online"
      ? Video
      : option.icon === "at-my-location"
      ? MapPin
      : Armchair;

  const cardStyle = {
    "--theme-color": theme.primary,
    borderColor: theme.primary,
    backgroundColor: isSelected ? theme.selectedBg : "rgba(255,255,255,0.6)",
    boxShadow: isSelected ? theme.selectedShadow : undefined,
  } as CSSProperties;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={`Select ${option.name}`}
      className={`
        group flex w-full items-start gap-2.5 overflow-hidden rounded-[10px] border p-4 md:p-5
        text-left transition-all duration-200 ease-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-color)] focus-visible:ring-offset-2
        active:scale-[0.99]
        backdrop-blur-md
        ${isSelected ? "" : "hover:bg-white/80"}
      `}
      style={cardStyle}
    >
      {/* Icon Circle */}
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white p-2 md:size-[45px] md:p-3">
        <IconComponent
          className="size-5 md:size-6"
          style={{ color: theme.primary }}
          aria-hidden="true"
        />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col pt-1">
        <div className="p-1.5 md:p-2.5">
          <span className="font-onest text-lg font-bold leading-none text-black md:text-2xl">
            {option.name}
          </span>
        </div>
        <div className="p-1.5 md:p-2.5">
          <span className="font-onest text-sm font-normal leading-tight text-black md:text-base md:leading-none">
            {option.description}
          </span>
        </div>
        {option.address && (
          <div className="flex items-start gap-1.5 p-1.5 md:p-2.5">
            <MapPin
              className="mt-0.5 size-3.5 shrink-0 md:size-4"
              style={{ color: theme.primary }}
              aria-hidden="true"
            />
            <span className="font-onest text-xs font-normal leading-snug text-black/60 md:text-sm">
              {option.address}
            </span>
          </div>
        )}
      </div>
    </button>
  );
};

const GOOGLE_MAPS_LIBRARIES: ("places")[] = ["places"];

// Location Search with Map Component
const LocationSearchWithMap = ({
  address,
  onAddressChange,
  theme,
  onCalculateLogistics,
  logistics,
  isCalculating,
}: {
  address: string;
  onAddressChange: (value: string) => void;
  theme: ThemePalette;
  onCalculateLogistics?: () => void;
  logistics?: LogisticsResponse | null;
  isCalculating?: boolean;
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GMAPAPI || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.formatted_address) {
      onAddressChange(place.formatted_address);
    } else if (place?.name) {
      onAddressChange(place.name);
    }
  };

  const mapUrl = address
    ? `https://www.google.com/maps/embed/v1/place?key=${process.env.NEXT_PUBLIC_GMAPAPI || ""}&q=${encodeURIComponent(address)}`
    : `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3312.123456789!2d151.2!3d-33.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDU0JzAwLjAiUyAxNTHCsDEyJzAwLjAiRQ!5e0!3m2!1sen!2sau!4v1234567890`;

  return (
    <div
      className="flex w-full flex-col gap-5 rounded-[10px] border p-10"
      style={{ borderColor: theme.primary }}
    >
      {/* Location Search Input */}
      <div className="flex flex-col gap-2.5">
        <div className="p-2.5">
          <span
            className="font-onest text-lg font-bold leading-[1.1]"
            style={{ color: theme.heading }}
          >
            Select Your Location
          </span>
        </div>
        <div className="flex gap-2">
          <div
            className="flex-1 rounded-[10px] border p-2.5"
            style={{ borderColor: theme.softBorder }}
          >
            {isLoaded ? (
              <Autocomplete
                onLoad={(ac) => { autocompleteRef.current = ac; }}
                onPlaceChanged={handlePlaceChanged}
                options={{ componentRestrictions: { country: "au" } }}
              >
                <input
                  type="text"
                  defaultValue={address}
                  placeholder="Search your address"
                  className="w-full bg-transparent p-2.5 font-onest text-base font-normal text-black placeholder:text-black/40 focus:outline-none"
                  aria-label="Search your address"
                />
              </Autocomplete>
            ) : (
              <input
                type="text"
                value={address}
                onChange={(e) => onAddressChange(e.target.value)}
                placeholder="Search your address"
                className="w-full bg-transparent p-2.5 font-onest text-base font-normal text-black placeholder:text-black/40 focus:outline-none"
                aria-label="Search your address"
                disabled
              />
            )}
          </div>
          {onCalculateLogistics && (
            <button
              type="button"
              onClick={onCalculateLogistics}
              disabled={!address || isCalculating}
              className="flex items-center justify-center rounded-[10px] px-6 py-2.5 font-onest text-base font-medium text-white transition-colors disabled:opacity-50"
              style={{
                backgroundColor: theme.primary,
              }}
            >
              {isCalculating ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                "Calculate"
              )}
            </button>
          )}
        </div>
      </div>

      {/* Logistics Result */}
      {logistics && (
        <div
          className="rounded-[10px] p-4"
          style={{ backgroundColor: theme.softBg }}
        >
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex flex-col">
              <span className="font-onest text-sm text-black/60">Distance</span>
              <span
                className="font-onest text-lg font-bold"
                style={{ color: theme.primary }}
              >
                {logistics.distance_km.toFixed(1)} km
              </span>
            </div>
            <div className="flex flex-col">
              <span className="font-onest text-sm text-black/60">
                Travel Time
              </span>
              <span
                className="font-onest text-lg font-bold"
                style={{ color: theme.primary }}
              >
                {logistics.duration_minutes} min
              </span>
            </div>
            <div className="flex flex-col">
              <span className="font-onest text-sm text-black/60">
                Travel Fee
              </span>
              <span
                className="font-onest text-lg font-bold"
                style={{ color: theme.primary }}
              >
                ${logistics.travel_fee.toFixed(2)} AUD
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <div className="relative h-[400px] w-full overflow-hidden rounded-[10px] md:h-[602px]">
        <iframe
          src={mapUrl}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Location Map"
          className="rounded-[10px]"
        />
      </div>
    </div>
  );
};

const ServiceLocation = ({
  title = "Choose your Service Location",
  subtitle = "Select the service that best matches your needs and explore support for wellbeing, relationships and daily life challenges.",
  options,
  defaultSelectedId,
  onOptionSelect,
  showAtMyLocation = false,
  onLocationAddressChange,
  defaultLocationAddress = "",
  useRedTheme = false,
  serviceTheme,
}: ServiceLocationProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(
    defaultSelectedId || null
  );
  const [locationAddress, setLocationAddress] = useState(defaultLocationAddress);
  const [logistics, setLogistics] = useState<LogisticsResponse | null>(null);

  // Logistics calculation mutation
  const [calculateLogistics, { isLoading: isCalculating }] =
    useCalculateLogisticsMutation();

  const resolvedTheme =
    (serviceTheme ? themePalettes[serviceTheme] : undefined) ||
    (useRedTheme ? themePalettes.psychology : undefined) ||
    defaultThemePalette;

  const officeAddress =
    serviceTheme === "occupationalTherapy" || serviceTheme === "psychology"
      ? "Suite 15.01, Level 15, 60 Station St East, Parramatta NSW 2150"
      : "Suite 207a, 30 Campbell St, Blacktown NSW 2148, Australia";

  const baseOptions = options || (showAtMyLocation ? ndisOptions : defaultOptions);
  const displayOptions = baseOptions.map((opt) =>
    opt.icon === "in-person" ? { ...opt, address: officeAddress } : opt
  );

  const handleSelect = (option: LocationOption) => {
    setSelectedId(option.id);
    onOptionSelect?.(option);
    // Reset logistics when changing selection
    if (option.id !== "at-my-location") {
      setLogistics(null);
    }
  };

  const handleAddressChange = (value: string) => {
    setLocationAddress(value);
    // Reset logistics when address changes
    setLogistics(null);
  };

  const handleCalculateLogistics = useCallback(async () => {
    if (!locationAddress) return;

    try {
      const result = await calculateLogistics({
        delivery_address: locationAddress,
      }).unwrap();

      if (result?.data) {
        const logisticsData = result.data as LogisticsResponse;
        setLogistics(logisticsData);
        // Notify parent with logistics data
        onLocationAddressChange?.({
          address: locationAddress,
          logistics: logisticsData,
        });
      }
    } catch (error) {
      console.error("Failed to calculate logistics:", error);
      // Still update address even if logistics calculation fails
      onLocationAddressChange?.({ address: locationAddress });
    }
  }, [locationAddress, calculateLogistics, onLocationAddressChange]);

  const showMap = selectedId === "at-my-location";

  // For NDIS flow with red theme, show different layout
  if (
    showAtMyLocation &&
    (serviceTheme === "occupationalTherapy" ||
      serviceTheme === "psychology" ||
      useRedTheme)
  ) {
    return (
      <div className="flex flex-col gap-5">
        {/* Header */}
        <div className="flex flex-col">
          <div className="p-2.5">
            <h2
              className="font-crimsonText text-2xl font-bold leading-tight sm:text-3xl md:text-[42px] md:leading-none"
              style={{ color: resolvedTheme.heading }}
            >
              {title}
            </h2>
          </div>
          <div className="w-full max-w-[565px] p-2.5">
            <p
              className="font-onest text-base font-normal leading-[1.1] md:text-lg"
              style={{ color: resolvedTheme.heading }}
            >
              {subtitle}
            </p>
          </div>
        </div>

        {/* Location Options */}
        <div className="p-[30px]">
          <div
            className="grid grid-cols-1 gap-6 md:grid-cols-2"
            role="radiogroup"
            aria-label="Service location options"
          >
            {displayOptions.map((option) => (
              <LocationOptionCard
                key={option.id}
                option={option}
                isSelected={selectedId === option.id}
                onSelect={() => handleSelect(option)}
                theme={resolvedTheme}
              />
            ))}
          </div>
        </div>

        {/* Location Search with Map - only shown when "At my location" is selected */}
        {showMap && (
          <div className="px-[30px] pb-[30px]">
            <LocationSearchWithMap
              address={locationAddress}
              onAddressChange={handleAddressChange}
              theme={resolvedTheme}
              onCalculateLogistics={handleCalculateLogistics}
              logistics={logistics}
              isCalculating={isCalculating}
            />
          </div>
        )}
      </div>
    );
  }

  // Default layout (original design)
  return (
    <div className="flex flex-col gap-10">
      {/* Header */}
      <div className="flex flex-col">
        <div className="p-2.5">
          <h2
            className="font-crimsonText text-2xl font-bold leading-tight sm:text-3xl md:text-[42px] md:leading-none"
            style={{ color: resolvedTheme.heading }}
          >
            {title}
          </h2>
        </div>
        <div className="w-full max-w-[565px] p-2.5">
          <p
            className="font-onest text-base font-normal leading-[1.2] md:text-lg md:leading-[1.1]"
            style={{ color: resolvedTheme.heading }}
          >
            {subtitle}
          </p>
        </div>
      </div>

      {/* Main Card with Background Image */}
      <div
        className="relative w-full overflow-hidden rounded-[10px] border bg-white lg:h-[550px]"
        role="radiogroup"
        aria-label="Service location options"
        style={{ borderColor: resolvedTheme.primary }}
      >
        {/* Background Image - positioned to the right, hidden on mobile */}
        <div className="absolute right-0 top-0 hidden h-full w-[70%] lg:block">
          <Image
            src="/images/v3/booking/location/30847ff6a99a7714bf565b5ffe4b770d1d1b79fe.jpg"
            alt=""
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 0px, 70vw"
            priority
          />
        </div>

        {/* Gradient fade from white to transparent on the left side - hidden on mobile */}
        <div
          className="absolute inset-y-0 left-0 hidden w-[50%] lg:block"
          style={{
            background:
              "linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0.98) 60%, rgba(255,255,255,0.8) 80%, transparent 100%)",
          }}
        />

        {/* Options Container */}
        <div className="relative flex w-full flex-col p-4 sm:p-6 md:p-8 lg:absolute lg:bottom-0 lg:left-0 lg:w-[60%] lg:p-[100px]">
          <div className="flex flex-col gap-4 md:gap-6">
            {displayOptions.map((option) => (
              <LocationOptionCard
                key={option.id}
                option={option}
                isSelected={selectedId === option.id}
                onSelect={() => handleSelect(option)}
                theme={resolvedTheme}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceLocation;
